import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Toast from '../../components/UI/Toast';
import {
    BellIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    CheckIcon,
    XMarkIcon,
    TrashIcon,
    EyeIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    ClockIcon,
    ArrowPathIcon,
    DocumentTextIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline';

interface Notification {
    _id: string;
    type: string;
    title: string;
    message: string;
    isRead: boolean;
    readAt?: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    actionRequired: boolean;
    actionUrl?: string;
    actionText?: string;
    expiresAt?: string;
    createdAt: string;
    company?: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    data?: {
        jobId?: string;
        taskId?: string;
        paymentId?: string;
        announcementId?: string;
        userId?: string;
        companyId?: string;
        customData?: any;
    };
}

interface NotificationStats {
    totalNotifications: number;
    unreadCount: number;
    stats: Array<{
        _id: string;
        count: number;
        unreadCount: number;
    }>;
}

const Notifications: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
    const [stats, setStats] = useState<NotificationStats>({
        totalNotifications: 0,
        unreadCount: 0,
        stats: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');

    const fetchNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications');
            setNotifications(response.data.data || []);
            setError(null);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to fetch notifications';
            setError(errorMessage);
            dispatch(showToast({ message: errorMessage, type: 'error' }));
            console.error('Error fetching notifications:', err);
        }
    }, [dispatch]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/notifications/stats');
            setStats(response.data.data);
        } catch (err: any) {
            console.error('Error fetching notification stats:', err);
        }
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchNotifications(), fetchStats()]);
        setRefreshing(false);
    };

    const markAsRead = async (notificationId: string) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);
            setNotifications(prev =>
                prev.map(notif =>
                    notif._id === notificationId
                        ? { ...notif, isRead: true, readAt: new Date().toISOString() }
                        : notif
                )
            );
            dispatch(showToast({ message: 'Notification marked as read', type: 'success' }));
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to mark notification as read';
            dispatch(showToast({ message: errorMessage, type: 'error' }));
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/mark-all-read');
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, isRead: true, readAt: new Date().toISOString() }))
            );
            dispatch(showToast({ message: 'All notifications marked as read', type: 'success' }));
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to mark all notifications as read';
            dispatch(showToast({ message: errorMessage, type: 'error' }));
        }
    };

    const deleteNotification = async (notificationId: string) => {
        try {
            await api.delete(`/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
            dispatch(showToast({ message: 'Notification deleted', type: 'success' }));
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to delete notification';
            dispatch(showToast({ message: errorMessage, type: 'error' }));
        }
    };

    useEffect(() => {
        if (user) {
            setLoading(true);
            Promise.all([fetchNotifications(), fetchStats()]).finally(() => setLoading(false));
        }
    }, [user, fetchNotifications, fetchStats]);

    // Apply filters
    useEffect(() => {
        let filtered = notifications.filter(notification => {
            const matchesSearch = searchTerm === '' ||
                notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.message.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesType = typeFilter === 'all' || notification.type === typeFilter;
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'read' && notification.isRead) ||
                (statusFilter === 'unread' && !notification.isRead);
            const matchesPriority = priorityFilter === 'all' || notification.priority === priorityFilter;

            return matchesSearch && matchesType && matchesStatus && matchesPriority;
        });
        setFilteredNotifications(filtered);
    }, [notifications, searchTerm, typeFilter, statusFilter, priorityFilter]);

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'job-invite':
            case 'job-application':
                return <DocumentTextIcon className="h-5 w-5" />;
            case 'task-assigned':
            case 'task-completed':
            case 'task-approved':
            case 'task-rejected':
                return <CheckCircleIcon className="h-5 w-5" />;
            case 'payment-approved':
            case 'payment-completed':
                return <CurrencyDollarIcon className="h-5 w-5" />;
            case 'announcement':
                return <InformationCircleIcon className="h-5 w-5" />;
            case 'system-update':
                return <ExclamationTriangleIcon className="h-5 w-5" />;
            case 'profile-update':
            case 'company-update':
                return <UserGroupIcon className="h-5 w-5" />;
            default:
                return <BellIcon className="h-5 w-5" />;
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'high':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'low':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'job-invite':
            case 'job-application':
                return 'text-blue-600 dark:text-blue-400';
            case 'task-assigned':
            case 'task-completed':
            case 'task-approved':
            case 'task-rejected':
                return 'text-green-600 dark:text-green-400';
            case 'payment-approved':
            case 'payment-completed':
                return 'text-yellow-600 dark:text-yellow-400';
            case 'announcement':
                return 'text-purple-600 dark:text-purple-400';
            case 'system-update':
                return 'text-red-600 dark:text-red-400';
            default:
                return 'text-gray-600 dark:text-gray-400';
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Error</h3>
                <p className="text-red-600 dark:text-red-400 mt-2">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="bg-white/10 rounded-lg p-3">
                            <BellIcon className="h-8 w-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Notifications</h1>
                            <p className="text-blue-100 mt-1">
                                Stay updated with your latest activities and updates
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="inline-flex items-center px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        {stats.unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                                <CheckIcon className="h-4 w-4 mr-2" />
                                Mark All Read
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-lg p-3">
                            <BellIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalNotifications}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-red-100 dark:bg-red-900 rounded-lg p-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Unread</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.unreadCount}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-green-100 dark:bg-green-900 rounded-lg p-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Read</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {stats.totalNotifications - stats.unreadCount}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-yellow-100 dark:bg-yellow-900 rounded-lg p-3">
                            <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Action Required</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {notifications.filter(n => n.actionRequired && !n.isRead).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search notifications..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Types</option>
                        <option value="job-invite">Job Invites</option>
                        <option value="job-application">Job Applications</option>
                        <option value="task-assigned">Task Assigned</option>
                        <option value="task-completed">Task Completed</option>
                        <option value="payment-approved">Payment Approved</option>
                        <option value="announcement">Announcements</option>
                        <option value="system-update">System Updates</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Status</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Priorities</option>
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                </div>
            </div>

            {/* Notifications List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Notifications ({filteredNotifications.length})
                        </h3>
                    </div>

                    {filteredNotifications.length === 0 ? (
                        <div className="text-center py-8">
                            <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No notifications found</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {notifications.length === 0
                                    ? "You don't have any notifications yet."
                                    : "Try adjusting your search or filter criteria."
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredNotifications.map((notification) => (
                                <div
                                    key={notification._id}
                                    className={`border rounded-lg p-6 hover:shadow-md transition-shadow ${notification.isRead
                                            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                                            : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-4 flex-1">
                                            <div className={`p-2 rounded-lg ${getTypeColor(notification.type)}`}>
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h4 className={`text-lg font-semibold ${notification.isRead
                                                            ? 'text-gray-700 dark:text-gray-300'
                                                            : 'text-gray-900 dark:text-white'
                                                        }`}>
                                                        {notification.title}
                                                    </h4>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                                                        {notification.priority.toUpperCase()}
                                                    </span>
                                                    {notification.actionRequired && (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                            ACTION REQUIRED
                                                        </span>
                                                    )}
                                                </div>
                                                <p className={`text-sm mb-3 ${notification.isRead
                                                        ? 'text-gray-600 dark:text-gray-400'
                                                        : 'text-gray-700 dark:text-gray-300'
                                                    }`}>
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                                    <span>{formatDate(notification.createdAt)}</span>
                                                    {notification.company && (
                                                        <span>• {notification.company.companyName}</span>
                                                    )}
                                                    {notification.actionUrl && (
                                                        <a
                                                            href={notification.actionUrl}
                                                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                        >
                                                            {notification.actionText || 'View Details'}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2 ml-4">
                                            {!notification.isRead && (
                                                <button
                                                    onClick={() => markAsRead(notification._id)}
                                                    className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                                                    title="Mark as read"
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => deleteNotification(notification._id)}
                                                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors"
                                                title="Delete notification"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Toast />
        </div>
    );
};

export default Notifications;
